import { setEditorStore } from "@core-ui/stores/EditorStore";
import type { ArticleProviderType } from "@ext/articleProvider/logic/ArticleProvider";
import SmallEditor from "@ext/inbox/components/Editor/SmallEditor";
import type { ToolbarMenuProps } from "@ext/markdown/core/edit/components/Menu/Menus/Toolbar";
import getExtensions, { type GetExtensionsPropsOptions } from "@ext/markdown/core/edit/logic/getExtensions";
import ElementGroups from "@ext/markdown/core/element/ElementGroups";
import Comment from "@ext/markdown/elements/comment/edit/model/comment";
import type { Extensions, JSONContent } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import { useEffect, useMemo } from "react";

interface TemplateEditorProps {
	title: string;
	content: JSONContent;
	id: string;
	extensions?: Extensions;
	providerType: ArticleProviderType;
	extensionsOptions?: GetExtensionsPropsOptions;
	menuOptions?: ToolbarMenuProps;
	onUpdate: (id: string, content: JSONContent, title: string) => void;
}

const getCustomArticleExtensions = (extensions: Extensions, options?: GetExtensionsPropsOptions): Extensions => [
	...getExtensions(options),
	Comment,
	...extensions,
	Document.extend({
		content: `paragraph ${ElementGroups.block}+`,
	}),
];

const CustomArticleEditor = (props: TemplateEditorProps) => {
	const {
		title,
		content,
		id,
		extensions: customExtensions,
		extensionsOptions,
		onUpdate,
		menuOptions,
		providerType,
	} = props;

	useEffect(() => {
		setEditorStore({ isSmallEditor: true });
		return () => {
			setEditorStore({ isSmallEditor: false });
		};
	}, []);

	const extensions = useMemo(
		() => getCustomArticleExtensions(customExtensions, extensionsOptions),
		[customExtensions, extensionsOptions],
	);

	return (
		<SmallEditor
			articleType={providerType}
			content={content}
			extensions={extensions}
			id={id}
			options={{
				menuProps: { includeResources: false, isSmallEditor: true, ...menuOptions },
			}}
			props={{ title, content }}
			updateCallback={onUpdate}
		/>
	);
};

export default CustomArticleEditor;
